import GooglePlayStoreLink from "@ui-application/navigation/GooglePlayStoreLink/GooglePlayStoreLink.web";
import AppleAppStoreLink from "@ui-application/navigation/AppleAppStoreLink/AppleAppStoreLink.web";
import PageMaxWidth from "@ui-kit/layout/PageMaxWidth/PageMaxWidth.web";
import {
  FOOTER_DATA,
  SIMPLE_FOOTER_DATA,
} from "@shared/constants/footer/footer-data";
import Accordion from "@ui-kit/disclosure/Accordion/Accordion.web";
import Icon from "@shared/components/ui-kit/icons/Icon.web";
import Link from "@ui-kit/navigation/Link/Link.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import styled from "@emotion/styled";
import { useTranslation } from "@shared/utils/internationalization";
import { useRouter } from "next/router";
import { useCallback } from "react";
import breakpoints from "@shared/theme/props/breakpoints";
import NewsletterSignup from "../NewsletterSignup/NewsletterSignup.web";
import { FooterProps } from "./Footer.props";

const { sm } = breakpoints;

const GridWrapper = styled(Box)`
  @media only screen and (min-width: ${sm}px) {
    display: grid;
    grid-template-rows: repeat(3, auto);
    grid-template-columns: repeat(4, auto);
  }
`;

const NewsletterWrapper = styled.div`
  padding-bottom: 30px;
  display: none;

  @media only screen and (min-width: ${sm}px) {
    grid-column: 3/5;
    padding: unset;
  }
`;

const SubsectionWrapper = styled(Box)`
  grid-column: 1/5;
`;

const DesktopSubsections = styled(Box)`
  display: none;

  @media only screen and (min-width: ${sm}px) {
    margin: 40px 0 31px;
    justify-content: space-between;
    display: flex;
  }
`;

const StyledDownloadWrapper = styled(Flex)`
  .download-badge {
    width: 94px;
    height: 33px;

    &:first-of-type {
      margin-bottom: 20px;
    }
  }
`;

const AccordionHeadingText = styled.p`
  color: white;
  font-weight: 700;
  font-size: 16px;
  line-height: 19.2px;

  @media only screen and (min-width: ${sm}px) {
    font-size: 14px;
    line-height: 26px;
    padding-bottom: 16px;
  }
`;

const DownloadWrapper = styled.div`
  margin: 22px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  .download-badge {
    width: 117px;
    height: 36px;

    &:first-of-type {
      margin-right: 20px;
    }
  }

  @media only screen and (min-width: ${sm}px) {
    display: none;
  }
`;

const DownloadText = styled.p`
  color: white;
  margin-bottom: 20px;
  font-weight: 700;
  font-size: 14px;
  line-height: 26px;
`;

const SocialWrapper = styled(Flex)`
  max-width: 115px;
  justify-content: space-between;
  align-items: center;
  margin: 0 auto 18px;

  @media only screen and (min-width: ${sm}px) {
    grid-column: 1/2;
    grid-row: 1/1;
    margin: unset;
  }
`;

const CopyrightText = styled.p`
  text-align: center;
  color: white;
  font-size: 12px;
  line-height: 26px;

  @media only screen and (min-width: ${sm}px) {
    grid-column: 1/5;
  }
`;

const Footer = ({ isReactNativeApp = false }: FooterProps) => {
  const { t } = useTranslation("common");
  const { links, socialMedia } = FOOTER_DATA;
  const { links: simpleFooterLinks } = SIMPLE_FOOTER_DATA;
  const router = useRouter();

  /**
   * TODO: use props not hardcoded paths
   */
  const simplifiedFooterLinks = [
    "/[eventSlug]",
    "/e/[eventSlug]",
    "/event/[eventSlug]",
    "/m/[membershipSlug]",
    "/pass/[membershipSlug]",
    "/purchase-dynamic",
  ];
  const shouldShowSimpleFooter = simplifiedFooterLinks.includes(
    router.pathname
  );

  const getSocialIcon = (name: string) => {
    // Use semantic token for simple footer to support dark mode
    const color = shouldShowSimpleFooter ? "fg.muted" : "white"; // footer social icons are white regardless
    const iconSize = shouldShowSimpleFooter ? "20px" : "15px";

    switch (name) {
      case "Facebook":
        return (
          <Icon.FacebookLogo color={color} width={iconSize} height={iconSize} />
        );
      case "Twitter":
        return <Icon.XLogo color={color} width={iconSize} height={iconSize} />;
      case "LinkedIn":
        return (
          <Icon.LinkedInLogo color={color} width={iconSize} height={iconSize} />
        );
      case "Instagram":
        return (
          <Icon.InstagramLogo
            color={color}
            width={iconSize}
            height={iconSize}
          />
        );
      default:
        return null;
    }
  };

  const getLinkComponent = useCallback(
    (name: string, path: string) => {
      return (
        <Box mb={shouldShowSimpleFooter ? 0 : 4} key={name}>
          <Link
            href={path}
            color={shouldShowSimpleFooter ? "fg.muted" : "whiteAlpha.700"}
            fontSize="sm"
            _hover={{
              color: shouldShowSimpleFooter ? "primary.fg" : "whiteAlpha.900",
            }}
          >
            {t(name)}
          </Link>
        </Box>
      );
    },
    [shouldShowSimpleFooter, t]
  );

  if (isReactNativeApp) {
    return null;
  }

  return (
    <Box
      p={shouldShowSimpleFooter ? "24px 0" : "32px 0"}
      px="none"
      bg={shouldShowSimpleFooter ? "transparent" : "primary.bg"}
      width="100%"
      borderTop={shouldShowSimpleFooter ? "1px solid" : "none"}
      borderTopColor={shouldShowSimpleFooter ? "border" : undefined}
    >
      <PageMaxWidth>
        {shouldShowSimpleFooter ? (
          <Flex
            width="100%"
            color="fg.muted"
            flexDirection={{ base: "column", md: "row" }}
            alignItems="center"
            fontSize="0.875rem"
            lineHeight="150%"
            gap="md"
          >
            <Flex justifySelf={{ base: "center", md: "flex-start" }}>
              <Text pr="xl" borderRight="1px solid" borderRightColor="border">
                Showpass &copy; {new Date().getFullYear()}
              </Text>
              <Flex gap="4xl" ml="xl">
                {simpleFooterLinks.map((link) => {
                  const { name, path } = link;
                  return getLinkComponent(name, path);
                })}
              </Flex>
            </Flex>
            <Flex ml={{ md: "auto" }} gap="xl">
              {socialMedia &&
                socialMedia.map((social) => {
                  return (
                    <Link
                      href={social.link}
                      key={social.name}
                      aria-label={`Link for ${social.name}`}
                      target="_blank"
                    >
                      {getSocialIcon(social.name)}
                    </Link>
                  );
                })}
            </Flex>
          </Flex>
        ) : (
          <GridWrapper>
            <NewsletterWrapper>
              <NewsletterSignup
                title={t("Subscribe to Showpass to receive the latest news")}
                placeholder={t("Enter your email")}
                buttonText={t("Subscribe")}
              />
            </NewsletterWrapper>
            {links && (
              <SubsectionWrapper>
                <Accordion
                  collapsible
                  display={{ base: "block", sm: "none" }}
                  collapseOverflowVisible
                >
                  {links.map((category, index) => {
                    return (
                      <Accordion.Item
                        key={category.id}
                        borderColor="primary.700"
                        borderLeft="none"
                        borderRight="none"
                        borderRadius="none"
                        margin="none"
                        marginTop="none"
                        borderTop={index === 0 ? "none" : undefined}
                      >
                        <h2>
                          <Accordion.Trigger
                            color="bg"
                            backgroundColor="transparent"
                            borderRadius="none"
                            padding="20px 0"
                            _hover={{ backgroundColor: "transparent" }}
                            _focus={{ boxShadow: "none" }}
                          >
                            <Box flex="1" textAlign="left">
                              <AccordionHeadingText>
                                {category.name}
                              </AccordionHeadingText>
                            </Box>
                          </Accordion.Trigger>
                        </h2>
                        <Accordion.Content pl={0}>
                          {category.footer_links.map((link) => {
                            const { name, path } = link;
                            return getLinkComponent(name, path);
                          })}
                        </Accordion.Content>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
                <DesktopSubsections>
                  {links.map((category) => {
                    return (
                      <Box key={category.id}>
                        <AccordionHeadingText>
                          {category.name}
                        </AccordionHeadingText>
                        {category.footer_links.map((link) => {
                          const { name, path } = link;
                          return getLinkComponent(name, path);
                        })}
                      </Box>
                    );
                  })}
                  <Box>
                    <DownloadText>{t("Download the App")}</DownloadText>
                    <StyledDownloadWrapper flexDirection="column">
                      <AppleAppStoreLink className="download-badge" />
                      <GooglePlayStoreLink className="download-badge" />
                    </StyledDownloadWrapper>
                  </Box>
                </DesktopSubsections>
              </SubsectionWrapper>
            )}
            <DownloadWrapper>
              <DownloadText>{t("Download the App")}</DownloadText>
              <Box>
                <AppleAppStoreLink className="download-badge" />
                <GooglePlayStoreLink className="download-badge" />
              </Box>
            </DownloadWrapper>
            {socialMedia && (
              <SocialWrapper>
                {socialMedia.map((social) => {
                  return (
                    <Link
                      href={social.link}
                      key={social.name}
                      aria-label={`Link for ${social.name}`}
                    >
                      {getSocialIcon(social.name)}
                    </Link>
                  );
                })}
              </SocialWrapper>
            )}
            <CopyrightText>
              Showpass &copy; {new Date().getFullYear()}
            </CopyrightText>
          </GridWrapper>
        )}
      </PageMaxWidth>
    </Box>
  );
};

export default Footer;
